import React, { useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { useLanguage } from '../contexts/LanguageContext';
import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';

gsap.registerPlugin(ScrollTrigger);

// Image URLs
const IMAGES = {
  hero: '/8.png',
  longTermOwner: 'https://customer-assets.emergentagent.com/job_4992c407-209d-4ffe-8201-36194c9609e6/artifacts/awdqd40l_VetterTec%20Workshop_Push-in%20of%20tubes%20into%20the%20tube%20bundle.jpg',
  managementWork: 'https://customer-assets.emergentagent.com/job_4992c407-209d-4ffe-8201-36194c9609e6/artifacts/l60db05f_VetterTec%20Workshop_Mechanic%20at%20Screw%20Press%20basket.JPG',
  industrialAngle: 'https://customer-assets.emergentagent.com/job_4992c407-209d-4ffe-8201-36194c9609e6/artifacts/3u9oh152_image00014.jpeg',
};

export const Investing = () => {
  const { language } = useLanguage();
  const bannerRef = useRef(null);
  const imageRef = useRef(null);

  useEffect(() => {
    if (imageRef.current) {
      gsap.to(imageRef.current, {
        yPercent: 20,
        ease: 'none',
        scrollTrigger: {
          trigger: bannerRef.current,
          start: 'top top',
          end: 'bottom top',
          scrub: 1
        }
      });
    }

    return () => {
      ScrollTrigger.getAll().forEach(trigger => trigger.kill());
    };
  }, []);

  const ctaButtons = [
    { label: language === 'fr' ? 'INVESTIR AVEC NOUS' : 'INVEST WITH US', link: '/contact?type=invest' },
    { label: language === 'fr' ? 'TRAVAILLER AVEC NOUS' : 'WORK WITH US', link: '/contact?type=work' },
    { label: language === 'fr' ? 'DEVENIR PARTENAIRE' : 'PARTNER WITH US', link: '/contact?type=partner' }
  ];

  return (
    <div className="min-h-screen bg-technical-white">
      {/* Floating CTA Buttons */}
      <div className="fixed right-8 top-1/3 z-40 flex flex-col gap-3 hidden lg:flex">
        {ctaButtons.map((btn, idx) => (
          <Link
            key={idx}
            to={btn.link}
            className="text-white text-xs font-bold tracking-wider px-5 py-3 bg-light-blue hover:bg-abyssal-blue transition-all duration-300 shadow-lg whitespace-nowrap"
            style={{ fontFamily: "'Novecento Sans Wide', 'Arial', sans-serif", letterSpacing: '1px', fontSize: '10px' }}
          >
            {btn.label}
          </Link>
        ))}
      </div>

      {/* Hero Banner */}
      <section
        ref={bannerRef}
        className="relative h-[500px] flex flex-col overflow-hidden"
        style={{ backgroundColor: '#0a1f35' }}
      >
        <div
          ref={imageRef}
          className="absolute inset-0 bg-cover bg-center scale-110"
          style={{ backgroundImage: `url(${IMAGES.hero})` }}
        />
        <div className="absolute inset-0 bg-abyssal-blue/50" />

        <div className="relative z-10 flex-1 flex flex-col items-center justify-center px-4">
          <h1
            className="text-5xl lg:text-6xl text-white mb-8 text-center"
            style={{
              fontFamily: "'Bank Gothic', 'Impact', sans-serif",
              fontWeight: 900,
              letterSpacing: '0.03em',
              textTransform: 'uppercase'
            }}
          >
            {language === 'fr' ? "APPROCHE D'INVESTISSEMENT" : 'INVESTMENT APPROACH'}
          </h1>
        </div>
      </section>

      {/* Page Title Section */}
      <section className="py-16 lg:py-20 bg-white">
        <div className="max-w-[1200px] mx-auto px-8 lg:px-12 text-center">
          <h2
            className="text-2xl lg:text-3xl text-abyssal-blue"
            style={{
              fontFamily: "'Novecento Sans Wide', 'Arial', sans-serif",
              fontWeight: 600,
              letterSpacing: '0.02em'
            }}
          >
            {language === 'fr'
              ? "Plateforme d'investissement focalisée sur les secteurs clés et les acteurs de l'industrie"
              : "Long term investor in key players in strategic industrial sectors"}
          </h2>
          <div className="w-24 h-1 bg-light-blue mx-auto mt-8"></div>
          <p
            className="text-lg lg:text-xl text-steel-grey text-center max-w-3xl mx-auto mt-8"
            style={{ fontFamily: "'Eurostile', 'Arial', sans-serif", lineHeight: 1.6 }}
          >
            {language === 'fr'
              ? "Nous investissons sur le long terme pour permettre une transformation profonde et durable."
              : "We invest over the long term to enable deep and lasting transformation."}
          </p>
        </div>
      </section>

      {/* Section 1: Long-term owner - Text LEFT, Image RIGHT */}
      <section className="py-16 lg:py-24 bg-technical-white">
        <div className="max-w-[1200px] mx-auto px-8 lg:px-12">
          <div className="grid lg:grid-cols-2 gap-12 lg:gap-20 items-center">
            {/* Text LEFT */}
            <div>
              <h3
                className="text-xl lg:text-2xl text-abyssal-blue mb-6"
                style={{ fontFamily: "'Novecento Sans Wide', 'Arial', sans-serif", fontWeight: 600, letterSpacing: '0.02em' }}
              >
                {language === 'fr' ? 'PROPRIÉTAIRE À LONG TERME' : 'LONG TERM OWNER'}
              </h3>
              <p
                className="text-lg text-steel-grey mb-6"
                style={{ fontFamily: "'Eurostile', 'Arial', sans-serif", lineHeight: 1.9 }}
              >
                {language === 'fr'
                  ? "Nous sommes un investisseur à long terme à travers les périodes de transformation et au-delà."
                  : "We are a long-term owner through periods of transformation and beyond."}
              </p>
              <p
                className="text-lg text-steel-grey mb-6"
                style={{ fontFamily: "'Eurostile', 'Arial', sans-serif", lineHeight: 1.9 }}
              >
                {language === 'fr'
                  ? "Nous investissons dans des entreprises en transformation, transformation que nous aidons à concevoir, piloter et exécuter."
                  : "We invest in businesses undergoing transformation, transformation we help design, manage, and execute."}
              </p>
              <p
                className="text-lg text-steel-grey"
                style={{ fontFamily: "'Eurostile', 'Arial', sans-serif", lineHeight: 1.9 }}
              >
                {language === 'fr'
                  ? "Nous détenons nos investissements au-delà de la phase de changement : nous continuons à détenir les entreprises après leur transformation car nous savons que les résultat de ces efforts prennent du temps à se matérialiser pleinement."
                  : "We hold our investments beyond the horizon: we continue to own businesses after their transformation because we know the dividends of that work take years to materialise."}
              </p>
            </div>

            {/* Image RIGHT */}
            <div className="overflow-hidden group">
              <img
                src={IMAGES.longTermOwner}
                alt="Long-term ownership"
                className="w-full h-[350px] lg:h-[450px] object-cover transition-transform duration-700 group-hover:scale-105"
                loading="lazy"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Section 2: Management work - Image LEFT, Text RIGHT */}
      <section className="py-16 lg:py-24 bg-white">
        <div className="max-w-[1200px] mx-auto px-8 lg:px-12">
          <div className="grid lg:grid-cols-2 gap-12 lg:gap-20 items-center">
            {/* Image LEFT */}
            <div className="overflow-hidden group lg:order-1">
              <img
                src={IMAGES.managementWork}
                alt="Working with management"
                className="w-full h-[350px] lg:h-[450px] object-cover transition-transform duration-700 group-hover:scale-105"
                loading="lazy"
              />
            </div>

            {/* Text RIGHT */}
            <div className="lg:order-2">
              <h3
                className="text-xl lg:text-2xl text-abyssal-blue mb-6"
                style={{ fontFamily: "'Novecento Sans Wide', 'Arial', sans-serif", fontWeight: 600, letterSpacing: '0.02em' }}
              >
                {language === 'fr' ? 'À TRAVERS LES PÉRIODES DE TRANSFORMATION' : 'THROUGH PERIODS OF TRANSFORMATION'}
              </h3>
              <p
                className="text-lg text-steel-grey mb-6"
                style={{ fontFamily: "'Eurostile', 'Arial', sans-serif", lineHeight: 1.9 }}
              >
                {language === 'fr'
                  ? "Nous travaillons avec le management sur des problèmes concrets pendant les périodes de transformation."
                  : "We work with management on concrete problems during periods of transformation."}
              </p>
              <p
                className="text-lg text-steel-grey mb-6"
                style={{ fontFamily: "'Eurostile', 'Arial', sans-serif", lineHeight: 1.9 }}
              >
                {language === 'fr'
                  ? "Nous savons de par notre propre expérience la rapidité de prise de décision requise dans une entreprise de taille moyenne."
                  : "We understand first-hand the speed of decision-making required in a mid-market business."}
              </p>
              <p
                className="text-lg text-steel-grey"
                style={{ fontFamily: "'Eurostile', 'Arial', sans-serif", lineHeight: 1.9 }}
              >
                {language === 'fr'
                  ? "Nous soutenons la transformation sur le terrain, aux côtés du management, pour faire avancer le plan."
                  : "We support transformation on the ground, alongside management, to drive the plan forward."}
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Section 3: Industrial angle - Text LEFT, Image RIGHT */}
      <section className="py-16 lg:py-24 bg-technical-white">
        <div className="max-w-[1200px] mx-auto px-8 lg:px-12">
          <div className="grid lg:grid-cols-2 gap-12 lg:gap-20 items-center">
            {/* Text LEFT */}
            <div>
              <h3
                className="text-xl lg:text-2xl text-abyssal-blue mb-6"
                style={{ fontFamily: "'Novecento Sans Wide', 'Arial', sans-serif", fontWeight: 600, letterSpacing: '0.02em' }}
              >
                {language === 'fr' ? 'AVEC UN MANDAT D\'INVESTISSEMENT FLEXIBLE' : 'WITH A FLEXIBLE INVESTMENT MANDATE'}
              </h3>
              <p
                className="text-lg text-steel-grey mb-6"
                style={{ fontFamily: "'Eurostile', 'Arial', sans-serif", lineHeight: 1.9 }}
              >
                {language === 'fr'
                  ? "Notre angle est industriel, notre approche d'investissement s'adapte à la situation."
                  : "Our angle is industrial. Our investment approach adapts to the situation."}
              </p>
              <p
                className="text-lg text-steel-grey"
                style={{ fontFamily: "'Eurostile', 'Arial', sans-serif", lineHeight: 1.9 }}
              >
                {language === 'fr'
                  ? "Nous cherchons à investir dans des entreprises industrielles avec une expertise approfondie, une technologie complexe ou un produit différenciant. Nous pouvons le faire en tant que majoritaire, minoritaire ou dans le cadre d'une restructuration de bilan."
                  : "We look to invest in industrial companies with deep expertise, complex technology, or a differentiated product. We can do so as a majority or minority investor, or as part of a balance-sheet restructuring."}
              </p>
            </div>

            {/* Image RIGHT */}
            <div className="overflow-hidden group">
              <img
                src={IMAGES.industrialAngle}
                alt="Industrial investment approach"
                className="w-full h-[350px] lg:h-[450px] object-cover transition-transform duration-700 group-hover:scale-105"
                loading="lazy"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Bottom Spacer */}
      <div className="h-24"></div>
    </div>
  );
};
